import moment from 'moment';
import { find, each } from 'lodash';
import { AGENT_STATUSES, DATA_LOADING_STATES, METRIC_NAMES } from '@config/Consts';
import { RESOURCES as AGENT_MANAGER_RESOURCES } from '@core/clients/AgentManagerClient';
import { BOT_LIST_RESOURCES } from './Consts';

const botMetricsTemplate = () => ({
  id: null,
  metrics: [
    { metric: METRIC_NAMES.AGENT_VIEW_AGENT_NOTIFICATIONS,
      score: null,
      reasons: [],
    },
    { metric: METRIC_NAMES.AGENT_VIEW_AGENT_PICTURE,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_AGENT_STATUS,
      score: AGENT_STATUSES.OFFLINE,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_STATUS_REASON,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_AGENT_NAME,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_AGENT_GROUP,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_AGENT_SKILLS,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_PENDING_RATE,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_CONCLUDED_CONVERSATIONS,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_TRANSFER_RATE,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_CLOSED_RATE,
      score: null,
    },
    { metric: METRIC_NAMES.AGENT_VIEW_INTENTS_MATCHED_RATE,
      score: null,
    },
    { metric: METRIC_NAMES.CSAT,
      score: null,
    },
  ],
});

const setBotsMetrics = (state, botsMetrics) => {
  state.botsMetrics = [];
  each(botsMetrics, (botMetrics) => {
    const bot = botMetricsTemplate();
    bot.id = botMetrics.id;
    each(botMetrics.metrics, (metric) => {
      const metricData = find(bot.metrics, m => m.metric === metric.metric);
      if (metricData) {
        metricData.score = metric.score;
      }
    });
    each(Object.keys(state.lastResponses), (key) => {
      const botMetric = find(state.lastResponses[key], botData =>
        botData.id === bot.id);
      if (botMetric) {
        each(botMetric.metrics, (metric) => {
          const metricData = find(bot.metrics, m => m.metric === metric.metric);
          if (metricData) {
            metricData.score = metric.score;
          }
        });
      }
    });
    state.botsMetrics.push(bot);
  });
};

const setDataStatus = (state, value) => {
  state.dataStatus[value.resourceName] = value.status;
};

const isPrimaryResource = resource => resource === AGENT_MANAGER_RESOURCES.AGENT_VIEW;

/**
 * relevant only for cSat and agent-view (no statuses)
 * @param state
 * @param data - an object with the following keys:
 * - botsMetrics: the list of the metrics per bot returned by the api
 * - resourceName: the name of the resource
 * - dataStatus: the status of the resource
 */
const updateBotsMetrics = (state, data) => {
  setDataStatus(state, { resourceName: data.resourceName, status: data.dataStatus });
  if (data.dataStatus === DATA_LOADING_STATES.SUCCESS) {
    state.lastTimeDataUpdated[data.resourceName] = moment();

    if (isPrimaryResource(data.resourceName)) {
      setBotsMetrics(state, data.botsMetrics);
    } else {
      state.lastResponses[data.resourceName] = data.botsMetrics;
      if (state.botsMetrics && state.botsMetrics.length > 0) {
        each(data.botsMetrics, (botMetric) => {
          const currentBot = find(state.botsMetrics, bot => bot.id === botMetric.id);
          if (currentBot) {
            each(botMetric.metrics, (metric) => {
              const metricData = find(currentBot.metrics, m => m.metric === metric.metric);
              if (metricData && (metricData.score !== metric.score)) {
                metricData.score = metric.score;
              }
            });
          }
        });
      }
    }
  }
};

const setWidgetMode = (state, value) => {
  state.widgetMode = value;
};

const setStatusFilter = (state, selectedStatuses) => {
  state.filter.status = selectedStatuses;
};

const setBotsFilter = (state, selectedBots) => {
  state.filter.bots = selectedBots;
};

const setBotStatusSummary = (state, data) => {
  setDataStatus(state, {
    resourceName: BOT_LIST_RESOURCES.METRICS,
    status: data.dataStatus,
  });
  if (data.dataStatus === DATA_LOADING_STATES.SUCCESS) {
    state.lastTimeDataUpdated[BOT_LIST_RESOURCES.METRICS] = moment();
    Object.keys(AGENT_STATUSES).forEach((status) => {
      state.botStatusSummary[AGENT_STATUSES[status]] =
        data.statusSummary[AGENT_STATUSES[status]];
    });
  }
};

const setSort = (state, sort) => {
  state.sort = sort;
};

const setCurrentPageBotIds = (state, currentPageBotIds) => {
  state.currentPageBotIds = currentPageBotIds;
};

const setGraphActivated = (state, isActivated) => {
  state.graphActivated = isActivated;
};

const setGraphFocusBotId = (state, botId) => {
  state.graphFocusBotId = botId;
};

const updateGraphMetrics = (state, metricsObject) => {
  state.lastTimeDataUpdated[BOT_LIST_RESOURCES.HISTORICAL] = moment();
  state.dataStatus[BOT_LIST_RESOURCES.HISTORICAL] = metricsObject.dataStatus;
  state.graphMetrics = metricsObject.graphMetrics;
};

const setGraphSelectedLegend = (state, selectedLegend) => {
  state.graphSelectedLegend = selectedLegend;
};

const setPresetValues = (state, preset) => {
  setWidgetMode(state, preset.widgetMode);
  setStatusFilter(state, preset.statusFilter);
  setBotsFilter(state, preset.botsFilter);
  setSort(state, preset.sort);
};

export default {
  botMetricsTemplate,
  setBotsMetrics,
  updateBotsMetrics,
  setDataStatus,
  setWidgetMode,
  setStatusFilter,
  setBotsFilter,
  setBotStatusSummary,
  setSort,
  setCurrentPageBotIds,
  setGraphActivated,
  setGraphFocusBotId,
  updateGraphMetrics,
  setGraphSelectedLegend,
  setPresetValues,
};
